import * as Yup from 'yup';
import { errorMessages } from '~/constants/errorMessages.ts';
import { regularExpressions } from '~/constants/regularExpressions.ts';
import { parseDate } from '~/helpers/utils';
import {
  documentNumberSchema,
  documentSeriesSchema,
  emailValidation,
  latinAndUkrCharactersOnly,
  phoneValidation,
} from '~/helpers/validation.ts';
import { TDocumentInfoForm, TManagerForm } from '~/modules/remote-banking-contacts/type.ts';
import { PersonRole } from '~/modules/remote-banking-contacts/constants.tsx';

export const contactInformationValidationSchema = Yup.object().shape({
  phoneNumber: phoneValidation,
  email: emailValidation,
});

export const managerValidationSchema: Yup.ObjectSchema<TManagerForm> = Yup.object({
  contragentType: Yup.string().required(errorMessages.ANSWER_OPTION),
  havePersonalIdentifier: Yup.boolean()
    .nullable()
    .when('contragentType', {
      is: PersonRole.LEGAL_ENTITY,
      then: (schema) => schema.required(errorMessages.ANSWER_OPTION),
      otherwise: (schema) => schema.notRequired(),
    }),
  contragentIdentifier: Yup.string()
    .nullable()
    .when('contragentType', {
      is: PersonRole.LEGAL_ENTITY,
      then: (schema) =>
        schema
          .required(errorMessages.FIELD_REQUIRED)
          .min(8, errorMessages.MAX_LENGTH(8))
          .matches(/^[0-9]+$/, errorMessages.MUST_BE_NUMBER)
          .test('not-all-same', errorMessages.INVALID_INPUT_DATA, (value) => {
            if (!value) return true;
            return !regularExpressions.DUPLICATION_NUMBER.test(value);
          }),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
  person: Yup.object({
    personalIdentifier: Yup.string()
      .nullable()
      .test('personalIdentifier-validation', errorMessages.FIELD_REQUIRED, function (value) {
        const contragentType = this.from?.[1]?.value?.contragentType;
        const havePersonalIdentifier = this.from?.[1]?.value?.havePersonalIdentifier;
        const isRequired = contragentType === PersonRole.SOLE_TRADER || !!havePersonalIdentifier;
        if (!isRequired) return true;

        try {
          Yup.string()
            .required(errorMessages.FIELD_REQUIRED)
            .min(10, errorMessages.MAX_LENGTH(10))
            .matches(/^[0-9]+$/, errorMessages.MUST_BE_NUMBER)
            .test(
              'not-repetitive-pattern',
              errorMessages.INVALID_INPUT_DATA,
              (value) => !value || !/(\d)\1{7,}/.test(value)
            )
            .validateSync(value);
          return true;
        } catch (e: any) {
          return this.createError({ message: e.message });
        }
      }),
    lastName: Yup.string()
      .required(errorMessages.FIELD_REQUIRED)
      .matches(regularExpressions.ONLY_TEXT, errorMessages.ONLY_TEXT)
      .concat(latinAndUkrCharactersOnly),
    firstName: Yup.string()
      .required(errorMessages.FIELD_REQUIRED)
      .matches(regularExpressions.ONLY_TEXT, errorMessages.ONLY_TEXT)
      .concat(latinAndUkrCharactersOnly),
    middleName: Yup.string()
      .nullable()
      .concat(latinAndUkrCharactersOnly)
      .transform((_, orin) => (orin?.trim() === '' ? null : orin))
      .matches(regularExpressions.ONLY_TEXT, errorMessages.ONLY_TEXT),
    birthDate: Yup.date()
      .transform((value, originalValue) => {
        if (!originalValue) return null;
        if (typeof originalValue === 'string') {
          const parsedDate = parseDate(originalValue);
          return parsedDate || new Date('');
        }
        return value;
      })
      .typeError(errorMessages.CORRECT_DATE_FORMAT)
      .required(errorMessages.FIELD_REQUIRED),
    documentInfo: Yup.mixed()
      .nullable()
      .test('documentInfo-validation', errorMessages.OPTION_REQUIRED, function (value) {
        const from = this.from as Array<{ value: TManagerForm }>;
        const havePersonalIdentifier = from?.[1]?.value?.havePersonalIdentifier;
        const contragentType = from?.[1]?.value?.contragentType;

        if (havePersonalIdentifier !== false || contragentType !== PersonRole.LEGAL_ENTITY)
          return true;
        if (!value)
          return this.createError({
            path: `${this.path}.documentTypeId`,
            message: errorMessages.OPTION_REQUIRED,
          });
        const { documentTypeId } = value as TDocumentInfoForm;

        if (!documentTypeId?.label) {
          return this.createError({
            path: `${this.path}.documentTypeId`,
            message: errorMessages.OPTION_REQUIRED,
          });
        }

        try {
          Yup.object({
            documentSeries: documentSeriesSchema('documentTypeId'),
            documentNumber: documentNumberSchema('documentTypeId'),
          }).validateSync(
            {
              documentTypeId: (value as TDocumentInfoForm).documentTypeId,
              documentSeries: (value as TDocumentInfoForm).documentSeries,
              documentNumber: (value as TDocumentInfoForm).documentNumber,
            },
            { abortEarly: false }
          );
          return true;
        } catch (e: any) {
          if (e.inner?.length) {
            return this.createError({
              path: `${this.path}.${e.inner[0].path}`,
              message: e.inner[0].message,
            });
          }
          return this.createError({ path: `${this.path}.${e.path}`, message: e.message });
        }
      }),
  }),
}) as Yup.ObjectSchema<TManagerForm>;

export const responsiblePersonsValidationSchema = Yup.object({
  formPersons: Yup.array().of(
    Yup.object({
      id: Yup.number().nullable(),
      lastName: Yup.string().nullable(),
      firstName: Yup.string().nullable(),
      middleName: Yup.string().nullable(),
      birthDate: Yup.string().nullable(),
      personalIdentifier: Yup.string().nullable(),
      personRole: Yup.string().nullable(),
      phoneNumber: phoneValidation,
      email: emailValidation,
    })
  ),
});
